import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './store/authStore';

// Pages
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Landing from './pages/Landing';
import StudentChat from './pages/StudentChat';
import StudentDashboard from './pages/StudentDashboard';
import TrainerDashboard from './pages/TrainerDashboard';
import AdminDashboard from './pages/AdminDashboard';
import Recharge from './pages/Recharge';
import PaymentReturn from './pages/PaymentReturn';
import Community from './pages/Community';
import Messages from './pages/Messages';
import SharedChat from './pages/SharedChat';
import TechniqueChat from './pages/TechniqueChat';
import ManageTechniques from './pages/ManageTechniques';
import Courses from './pages/Courses';
import CourseDetail from './pages/CourseDetail';
import LessonView from './pages/LessonView';
import ManageLMS from './pages/ManageLMS';
import AssessmentView from './pages/AssessmentView';
import Assessments from './pages/Assessments';
import Submissions from './pages/Submissions';
import LiveSessions from './pages/LiveSessions';
import MemoryProfile from './pages/MemoryProfile';
import MyProfile from './pages/MyProfile';
import Onboarding from './pages/Onboarding';
import PaymentPage from './pages/PaymentPage';
import PrivacyPolicy from './pages/PrivacyPolicy';

// Layout
import Layout from './components/common/Layout';
import ProtectedRoute from './components/common/ProtectedRoute';

function App() {
  const { isAuthenticated, user } = useAuthStore();

  // Redirect based on role
  const getDefaultRoute = () => {
    if (!isAuthenticated) return '/';
    switch (user?.role) {
      case 'admin': return '/admin';
      case 'trainer': return '/trainer';
      default:
        return '/chat';
    }
  };

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={
        isAuthenticated ? <Navigate to={getDefaultRoute()} /> : <Landing />
      } />
      <Route path="/login" element={
        isAuthenticated ? <Navigate to={getDefaultRoute()} /> : <Login />
      } />
      <Route path="/register" element={
        isAuthenticated ? <Navigate to={getDefaultRoute()} /> : <Register />
      } />

      <Route path="/forgot-password" element={
        isAuthenticated ? <Navigate to={getDefaultRoute()} /> : <ForgotPassword />
      } />
      <Route path="/reset-password" element={
        isAuthenticated ? <Navigate to={getDefaultRoute()} /> : <ResetPassword />
      } />

      {/* Public pages */}
      <Route path="/shared/:token" element={<SharedChat />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />

      {/* Onboarding (full-screen, no sidebar) */}
      <Route path="/onboarding" element={
        isAuthenticated ? <Onboarding /> : <Navigate to="/login" />
      } />

      {/* Payment page for users who completed onboarding but haven't paid */}
      <Route path="/payment" element={
        isAuthenticated ? <PaymentPage /> : <Navigate to="/login" />
      } />

      {/* Protected routes */}
      <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        {/* Student routes */}
        <Route path="/chat" element={<StudentChat />} />
        <Route path="/dashboard" element={<StudentDashboard />} />
        <Route path="/recharge" element={<Recharge />} />
        <Route path="/payment/return" element={<PaymentReturn />} />
        <Route path="/profile" element={<MyProfile />} />
        <Route path="/memory-profile" element={<MemoryProfile />} />

        {/* Community & Messages (all roles) */}
        <Route path="/community" element={<Community />} />
        <Route path="/messages" element={<Messages />} />

        {/* Technique-specific chat */}
        <Route path="/techniques/:slug" element={<TechniqueChat />} />

        {/* Manage Techniques (trainer/admin only) */}
        <Route path="/techniques/manage" element={
          <ProtectedRoute roles={['admin', 'trainer']}>
            <ManageTechniques />
          </ProtectedRoute>
        } />

        {/* LMS routes (all authenticated users) */}
        <Route path="/lms/courses" element={<Courses />} />
        <Route path="/lms/courses/:id" element={<CourseDetail />} />
        <Route path="/lms/lessons/:id" element={<LessonView />} />
        <Route path="/lms/assessments" element={<Assessments />} />
        <Route path="/lms/assessments/:id" element={<AssessmentView />} />
        <Route path="/lms/submissions" element={<Submissions />} />
        <Route path="/lms/live-sessions" element={<LiveSessions />} />

        {/* Manage LMS (admin only) */}
        <Route path="/lms/manage" element={
          <ProtectedRoute roles={['admin']}>
            <ManageLMS />
          </ProtectedRoute>
        } />

        {/* Trainer routes */}
        <Route path="/trainer" element={
          <ProtectedRoute roles={['admin', 'trainer']}>
            <TrainerDashboard />
          </ProtectedRoute>
        } />

        {/* Admin routes */}
        <Route path="/admin" element={
          <ProtectedRoute roles={['admin']}>
            <AdminDashboard />
          </ProtectedRoute>
        } />
      </Route>

      {/* Fallback - redirect to appropriate page */}
      <Route path="*" element={<Navigate to={getDefaultRoute()} />} />
    </Routes>
  );
}

export default App;
